import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Chip,
  Icon,
  Notice,
  Segmented,
  Skeleton,
  TextField,
  iconCall,
  iconCheck,
  iconWhatsapp,
  useSnackbar,
} from '@sauda/ui/kit';
import { PHOTO_CONTENT_TYPES } from '@sauda/shared';
import type { BrandTokens } from '@sauda/theme';
import { Screen, useChrome } from '../app/chrome.js';
import { ContactCard } from './ContactCard.js';
import { OwnerMessagesCard } from './OwnerMessagesCard.js';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { BRAND_PRESETS, normaliseColour, roleVariables } from '../lib/brand-presets.js';
import { MAX_PHOTO_BYTES } from '../lib/photo-upload.js';
import { formatRupees } from '../lib/money.js';
import type { PropertyView, UploadTarget } from '../lib/types.js';

interface BrandView {
  name: string;
  colour: string | null;
  contrastLevel: number | null;
  logoUrl: string | null;
  phone: string | null;
  whatsapp: string | null;
}

/**
 * The public face of the agency: name, logo and colour. The colour is chosen from eight presets
 * or typed, and the preview shows a property page in it, in light and in dark, as it will look.
 */
export function BrandScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const snack = useSnackbar();
  const file = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [colour, setColour] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [error, setError] = useState<string | null>(null);
  const [logoNote, setLogoNote] = useState<string | null>(null);

  // The preview shows the agency's own first flat when there is one, and says so when it is a sample.
  const flats = useQuery({
    queryKey: ['properties', auth.agencyId],
    queryFn: () => api<PropertyView[]>('/properties'),
  });
  const real = (flats.data ?? []).find((p) => p.status === 'available') ?? flats.data?.[0];
  const realPrice = real === undefined ? null : (real.rentPaise ?? real.pricePaise);

  const brand = useQuery({
    queryKey: ['brand', auth.agencyId],
    queryFn: () => api<BrandView>('/agency/brand'),
    // The logo is re-encoded by the worker: look again for a moment after an upload.
    refetchInterval: logoNote === null ? false : 3000,
  });
  const data = brand.data;
  const shownName = name ?? data?.name ?? '';
  const seed = colour ?? data?.colour ?? null;

  // The colour code is only needed here, so it loads with this screen and nowhere else.
  const [tokens, setTokens] = useState<BrandTokens | null>(null);
  useEffect(() => {
    if (seed === null) {
      setTokens(null);
      return;
    }
    let live = true;
    void import('@sauda/theme').then((theme) => {
      if (live) setTokens(theme.generateBrandTokens(seed));
    });
    return () => {
      live = false;
    };
  }, [seed]);
  const previewStyle = useMemo<CSSProperties | undefined>(
    () => (tokens === null ? undefined : (roleVariables(tokens[mode]) as CSSProperties)),
    [tokens, mode],
  );

  const save = useMutation({
    mutationFn: () =>
      api<BrandView>('/agency/brand', {
        method: 'PUT',
        body: {
          ...(name !== null && name.trim() !== data?.name ? { name: name.trim() } : {}),
          ...(colour !== null && colour !== data?.colour ? { colour } : {}),
        },
      }),
    onSuccess: async () => {
      setError(null);
      setName(null);
      setColour(null);
      setTyped('');
      await cache.invalidateQueries({ queryKey: ['brand'] });
      snack({ text: t('brand.saved') });
    },
    onError: () => setError(t('brand.failed')),
  });
  const logo = useMutation({
    mutationFn: async (picked: File) => {
      const target = await api<{ key: string; upload: UploadTarget['upload'] }>(
        '/agency/logo/uploads',
        { method: 'POST', body: { contentType: picked.type } },
      );
      const form = new FormData();
      for (const [field, value] of Object.entries(target.upload.fields)) form.append(field, value);
      form.append('file', picked);
      const res = await fetch(target.upload.url, {
        method: 'POST',
        body: form,
        credentials: 'omit',
      });
      if (!res.ok) throw new Error(`storage answered ${String(res.status)}`);
      await api('/agency/logo/confirm', { method: 'POST', body: { key: target.key } });
    },
    onSuccess: () => {
      setError(null);
      setLogoNote(t('brand.logoProcessing'));
      window.setTimeout(() => setLogoNote(null), 15_000);
    },
    onError: () => setError(t('brand.logoFailed')),
    onSettled: () => {
      if (file.current) file.current.value = '';
    },
  });
  const pickLogo = (picked: File | undefined) => {
    if (picked === undefined) return;
    if (
      !(PHOTO_CONTENT_TYPES as readonly string[]).includes(picked.type) ||
      picked.size > MAX_PHOTO_BYTES
    ) {
      setError(t('photos.skipped'));
      return;
    }
    logo.mutate(picked);
  };

  if (data === undefined)
    return (
      <Screen title={t('brand.title')} large eyebrow={auth.current?.agencyName}>
        {brand.isError ? (
          <Notice tone="error">{t('brand.failed')}</Notice>
        ) : (
          <Skeleton height={160} />
        )}
      </Screen>
    );
  const dirty =
    (name !== null && name.trim() !== data.name && name.trim().length >= 2) ||
    (colour !== null && colour !== data.colour);
  const typedColour = typed === '' ? null : normaliseColour(typed);
  return (
    <Screen title={t('brand.title')} large eyebrow={auth.current?.agencyName}>
      {error === null ? null : <Notice tone="error">{error}</Notice>}
      <Card>
        <TextField
          label={t('brand.name')}
          value={shownName}
          onChange={(e) => setName(e.target.value)}
        />
      </Card>
      <ContactCard phone={data.phone} whatsapp={data.whatsapp} readOnly={chrome.readOnly} />
      <OwnerMessagesCard readOnly={chrome.readOnly} />
      <Card>
        <h2 className="k-t-title-sm">{t('brand.colour')}</h2>
        <div className="swatches" role="group" aria-label={t('brand.presets')}>
          {BRAND_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              className="swatch k-layer"
              style={{ background: p.hex }} // lint-allow: inline-style
              aria-label={t(`brand.preset_${p.key}`)}
              aria-pressed={seed === p.hex}
              onClick={() => {
                setColour(p.hex);
                setTyped('');
              }}
            >
              {seed === p.hex ? <Icon icon={iconCheck} size={20} /> : null}
            </button>
          ))}
        </div>
        <TextField
          label={t('brand.custom')}
          supporting={typed !== '' && typedColour === null ? undefined : t('brand.customHelp')}
          error={typed !== '' && typedColour === null ? t('brand.badColour') : undefined}
          value={typed}
          placeholder="#0F766E" // lint-allow: colour
          onChange={(e) => {
            setTyped(e.target.value);
            const next = normaliseColour(e.target.value);
            if (next !== null) setColour(next);
          }}
        />
        {data.contrastLevel === 0.5 && colour === null ? (
          <Notice tone="info">{t('brand.contrastRaised')}</Notice>
        ) : null}
      </Card>
      <Card>
        <h2 className="k-t-title-sm">{t('brand.logo')}</h2>
        {data.logoUrl === null ? (
          <p className="k-t-body k-muted">{t('brand.noLogo')}</p>
        ) : (
          <img className="brand-logo" src={data.logoUrl} alt={t('brand.logoAlt')} height={64} />
        )}
        <input
          ref={file}
          type="file"
          className="k-sr-only"
          accept={PHOTO_CONTENT_TYPES.join(',')}
          aria-label={t('brand.chooseLogo')}
          onChange={(e) => pickLogo(e.target.files?.[0])}
        />
        {logoNote === null ? null : <Notice tone="success">{logoNote}</Notice>}
        <div>
          <Button
            variant="tonal"
            loading={logo.isPending}
            disabled={chrome.readOnly}
            onClick={() => file.current?.click()}
          >
            {data.logoUrl === null ? t('brand.addLogo') : t('brand.changeLogo')}
          </Button>
        </div>
      </Card>
      <section className="today-section" aria-label={t('brand.preview')}>
        <div className="card-head">
          <h2 className="k-t-title-sm">{t('brand.preview')}</h2>
          <Segmented
            label={t('brand.previewMode')}
            value={mode}
            onChange={(v) => setMode(v as 'light' | 'dark')}
            options={[
              { value: 'light', label: t('brand.light') },
              { value: 'dark', label: t('brand.dark') },
            ]}
          />
        </div>
        <div className="brand-preview" style={previewStyle} data-testid="brand-preview">
          <div className="brand-preview__head">
            {data.logoUrl === null ? null : (
              <img src={data.logoUrl} alt="" height={32} className="brand-logo" />
            )}
            <p className="k-t-title-sm">{shownName}</p>
          </div>
          {real?.photos[0] === undefined ? (
            <div className="brand-preview__photo" aria-hidden="true" />
          ) : (
            <img
              src={real.photos[0].thumbUrl}
              alt=""
              className="brand-preview__photo brand-preview__photo--image"
            />
          )}
          <p className="k-t-title-sm">{real?.title ?? t('brand.sampleTitle')}</p>
          <p className="k-t-money">{formatRupees(realPrice ?? 2_800_000)}</p>
          <p className="k-t-body-sm k-muted" data-testid="brand-preview-source">
            {real === undefined ? t('brand.previewSample') : t('brand.previewReal')}
          </p>
          <div className="chips-row">
            <span className="k-btn k-btn--tonal k-btn--md k-t-label-lg">
              <Icon icon={iconCall} size={18} />
              <span className="k-btn__label">{t('visits.call')}</span>
            </span>
            <span className="k-btn k-btn--tonal k-btn--md k-t-label-lg">
              <Icon icon={iconWhatsapp} size={18} />
              <span className="k-btn__label">{t('visits.whatsapp')}</span>
            </span>
          </div>
          <span
            className="k-btn k-btn--filled k-btn--lg k-btn--block k-t-label-lg"
            aria-hidden="true"
          >
            <span className="k-btn__label">{t('brand.sampleBook')}</span>
          </span>
          <Chip selected>{t('brand.sampleChip')}</Chip>
        </div>
      </section>
      <div className="step-bar">
        <Button
          size="lg"
          loading={save.isPending}
          disabled={!dirty || chrome.readOnly}
          onClick={() => save.mutate()}
        >
          {t('brand.save')}
        </Button>
      </div>
    </Screen>
  );
}
